import { orderBy } from 'lodash';
import { OrderConstant } from 'src/constants';
import {
  OrderActionDto,
  OrderDetailsDto,
  OrderDto,
  OrderDurationDto,
  OrderEventDto,
  OrderTypeDto,
  QuoteDto,
  OrderStatusIntDto,
} from 'src/dtos';
import { FromTradingBlockServerEvent, FromTradingBlockServerEventDto } from 'src/events';
import {
  Order,
  OrderAction,
  OrderActionLabel,
  OrderDetails,
  OrderDuration,
  OrderDurationLabel,
  OrderEvent,
  OrderEventStatus,
  OrderStatus,
  OrderStatusLabel,
  OrderType,
  OrderTypeLabel,
} from 'src/models';
import { Maybe } from 'src/utils';

const isPartialOrder = ({ quantity, filledQuantity }: { quantity: number; filledQuantity: number }): boolean =>
  Boolean(filledQuantity < quantity && filledQuantity > 0);

const findEstimatedOrderPrice = ({
  type,
  lastValue,
  limit,
  stop,
}: {
  type: OrderType;
  lastValue?: number;
  limit?: number;
  stop?: number;
}): Maybe<number> => {
  if (type.isLimit) {
    return limit;
  }

  if (type.isStopLimit) {
    return limit;
  }

  if (type.isStopMarket) {
    return stop;
  }

  return lastValue;
};

export const findOrderPrice = ({
  type,
  status,
  averageFillPrice,
  lastValue,
  limit,
  stop,
  isPartial,
}: {
  type: OrderType;
  status: OrderStatus;
  averageFillPrice?: number;
  lastValue?: number;
  limit?: number;
  stop?: number;
  isPartial: boolean;
}): Maybe<number> => {
  if (status.isCompleted || isPartial) {
    return averageFillPrice;
  }

  return findEstimatedOrderPrice({ type, lastValue, limit, stop });
};

export const findOrderCommission = ({
  status,
  commissionAssessed,
  isPartial,
}: {
  status: OrderStatus;
  commissionAssessed?: number;
  isPartial: boolean;
}): number => {
  if (status.isCompleted || isPartial) {
    return commissionAssessed ?? 0;
  }

  return OrderConstant.ESTIMATED_SERVICE_FEE;
};

export const findOrderTotalCost = ({
  status,
  action,
  quantity,
  price,
  fillQuantity,
  commission,
  isPartial,
}: {
  status: OrderStatus;
  action: OrderAction;
  quantity: number;
  price: number;
  fillQuantity: number;
  commission: number;
  isPartial: boolean;
}): number => {
  if (status.isCompleted || isPartial) {
    const totalCost = fillQuantity * price;

    return action.isBuy ? -(totalCost + commission) : totalCost - commission;
  }

  const totalCost = quantity * price;

  return action.isBuy
    ? -(totalCost + OrderConstant.ESTIMATED_SERVICE_FEE)
    : totalCost - OrderConstant.ESTIMATED_SERVICE_FEE;
};

const mapOrderEventDtoToModel = (dto: OrderEventDto): OrderEvent => ({
  description: dto.Description,
  fillPrice: dto.FillPrice,
  fillQuantity: dto.FillQuantity,
  limit: dto.Limit,
  status: new OrderEventStatus(dto.OrderStatus),
  stop: dto.Stop,
  symbol: dto.Symbol,
  createdAt: dto.TimeStamp,
});

export const mapOrderDtoToModel = (dto: OrderDto, quote?: QuoteDto): Order => {
  const isPartial = isPartialOrder({ quantity: dto.Quantity ?? 0, filledQuantity: dto.FillQuantity ?? 0 });
  const status = new OrderStatus(dto.OrderStatus);
  const type = new OrderType(dto.OrderType);
  const action = new OrderAction(dto.Legs?.[0]?.Action);
  const commission = findOrderCommission({ status, commissionAssessed: dto.CommissionAssessed, isPartial });
  const price =
    findOrderPrice({
      type,
      status,
      averageFillPrice: dto.AverageFillPrice,
      lastValue: quote?.pricedata?.last,
      limit: dto.Price,
      stop: dto.Stop,
      isPartial,
    }) ?? 0;
  const totalCost = findOrderTotalCost({
    status,
    action,
    price,
    fillQuantity: dto.FillQuantity ?? 0,
    quantity: dto.Quantity,
    commission,
    isPartial,
  });

  let result: Order = {
    id: dto.OrderId.toString(),
    symbol: dto.UnderlyingSymbol ?? '',
    description: quote?.longname ?? dto.UnderlyingSymbol ?? '',
    quantity: dto.Quantity,
    filledQuantity: dto.FillQuantity ?? 0,
    isPartial,
    price,
    limit: dto.Price,
    stop: dto.Stop,
    commission,
    totalCost,
    action: new OrderAction(dto.Legs?.[0]?.Action),
    status,
    type,
    duration: new OrderDuration(dto.Duration),
    updatedAt: dto.Date,
  };

  return result;
};

export const mapOrderDetailsDtoToModel = (dto: OrderDetailsDto, quote?: QuoteDto): OrderDetails => {
  const order = mapOrderDtoToModel(dto, quote);
  const events = orderBy(dto.OrderEvents?.map(mapOrderEventDtoToModel), ['createdAt'], ['desc']) ?? [];

  return {
    ...order,
    events,
  };
};

export const mapGetOrdersListResponseDtoToModel = (dtos: OrderDto[], quotes: QuoteDto[]): Order[] =>
  dtos.map(orderDto =>
    mapOrderDtoToModel(
      orderDto,
      quotes.find(aQuote => aQuote.symbol === orderDto.UnderlyingSymbol),
    ),
  );

export const mapOrderUpdateEventDtoToModel = (
  dto: FromTradingBlockServerEventDto.OrderUpdateEventDto,
): FromTradingBlockServerEvent.OrderUpdateEvent => {
  const result: FromTradingBlockServerEvent.OrderUpdateEvent = {
    type: FromTradingBlockServerEvent.EventType.ORDER_UPDATE,
    payload: {
      accountId: Number(dto[2]),
      id: dto[3],
      createdAt: dto[4],
      symbol: dto[5],
      quantity: Number(dto[6]),
      fillQuantity: Number(dto[7]),
      fillPrice: Number(dto[8]),
      action: new OrderAction(Number(dto[9])),
      status: new OrderEventStatus(Number(dto[10])),
      commission: Number(dto[15]),
    },
  };

  return result;
};

export const mapOrderTypeDtoToLabel = (type: OrderTypeDto): OrderTypeLabel => {
  switch (type) {
    case OrderTypeDto.Market:
      return OrderTypeLabel.Market;
    case OrderTypeDto.Limit:
      return OrderTypeLabel.Limit;
    case OrderTypeDto.StopMarket:
      return OrderTypeLabel.StopMarket;
    case OrderTypeDto.StopLimit:
      return OrderTypeLabel.StopLimit;
    case OrderTypeDto.MarketOnClose:
      return OrderTypeLabel.MarketOnClose;
    default:
      return OrderTypeLabel.Market;
  }
};

export const mapOrderActionDtoToLabel = (action: OrderActionDto): OrderActionLabel => {
  return action === OrderActionDto.Buy ? OrderActionLabel.Buy : OrderActionLabel.Sell;
};

export const mapOrderDurationDtoToLabel = (duration: OrderDurationDto): OrderDurationLabel => {
  return duration === OrderDurationDto.Day ? OrderDurationLabel.Day : OrderDurationLabel.GTC;
};

export const mapOrderStatusLabelToOrderStatusIdQueryParamDto = (status: OrderStatusLabel): string => {
  switch (status) {
    case OrderStatusLabel.COMPLETED:
      return `${OrderStatusIntDto.Filled}`;
    case OrderStatusLabel.CANCELLED:
      return [OrderStatusIntDto.Cancelled, OrderStatusIntDto.PartiallyCancelled].join(',');
    case OrderStatusLabel.REJECTED:
      return [
        OrderStatusIntDto.Rejected,
        OrderStatusIntDto.ReplaceRejected,
        OrderStatusIntDto.CancelRejected,
        OrderStatusIntDto.Suspended,
        OrderStatusIntDto.Expired,
        OrderStatusIntDto.Busted,
      ].join(',');
    default:
      return [
        OrderStatusIntDto.New,
        OrderStatusIntDto.PartiallyFilled,
        OrderStatusIntDto.PendingNew,
        OrderStatusIntDto.PendingCancel,
        OrderStatusIntDto.Scheduled,
        OrderStatusIntDto.CancelInitiated,
        OrderStatusIntDto.Stopped,
        OrderStatusIntDto.Calculated,
        OrderStatusIntDto.DoneForDay,
        OrderStatusIntDto.Saved,
        OrderStatusIntDto.Acknowledged,
        OrderStatusIntDto.Replaced,
        OrderStatusIntDto.PendingReplace,
        OrderStatusIntDto.LiveUntriggered,
        OrderStatusIntDto.OCO_Untriggered,
        OrderStatusIntDto.CancelledUntriggered,
        OrderStatusIntDto.Initiated,
        OrderStatusIntDto.ReplaceInitiated,
        OrderStatusIntDto.PreAllocated,
      ].join(',');
  }
};
